/**
 * iter206 — Admin: Social Monitor Pause History Dashboard
 *
 * Shows which platforms auto-pause most often so ops can prioritize
 * fetcher fixes. Aggregates data from `social_monitor_pause_events` via
 * two admin-only endpoints:
 *   • GET /api/admin/social-monitor/pause-stats?window_days=N
 *   • GET /api/admin/social-monitor/pause-events?limit=N&platform=X
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { AlertTriangle, Clock, TrendingDown, RefreshCw, Loader2, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 365 days' },
];

const relative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const PauseHistoryPage = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, evRes] = await Promise.all([
        api.get(`/admin/social-monitor/pause-stats?window_days=${windowDays}`),
        api.get(`/admin/social-monitor/pause-events?limit=100${
          platformFilter !== 'all' ? `&platform=${platformFilter}` : ''
        }`),
      ]);
      setStats(statsRes.data);
      setEvents(evRes.data.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pause history');
    } finally {
      setLoading(false);
    }
  }, [windowDays, platformFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const platformOptions = useMemo(() => {
    const known = new Set((stats?.per_platform || []).map((p) => p.platform));
    return ['all', ...Array.from(known)];
  }, [stats]);

  return (
    <div className="p-6 space-y-6" data-testid="admin-pause-history-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <TrendingDown className="h-6 w-6 text-rose-400" />
            Social Monitor Pause History
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Which platforms auto-pause most often — prioritize fetcher fixes for the top offenders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
            <SelectTrigger className="w-[160px]" data-testid="admin-pause-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.value} value={String(w.value)}>{w.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            disabled={loading}
            data-testid="admin-pause-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="admin-pause-total-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-400 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Total Pauses ({windowDays}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (stats?.total_pauses || 0)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="admin-pause-active-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-400 text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" /> Currently Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-rose-300">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (stats?.active_pauses || 0)}
            </div>
            <p className="text-xs text-slate-500 mt-1">Not yet resolved via reconnect</p>
          </CardContent>
        </Card>
        <Card data-testid="admin-pause-platforms-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-400 text-sm">Platforms Affected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (stats?.per_platform?.length || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* iter207 — Pause trend line chart. Ops use this to spot spikes
          (e.g. a fetcher regression that instantly broke on deploy) vs
          steady-state noise. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Pause Trend ({windowDays}d)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-rose-400" /></div>
          ) : !stats?.trend || stats.trend.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center" data-testid="admin-pause-empty-trend">
              No trend data yet.
            </p>
          ) : (
            <div className="h-64 w-full" data-testid="admin-pause-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2A4D" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={11}
                    tickFormatter={(d) => {
                      // Compact "Feb 03" tick label
                      const dt = new Date(d);
                      return dt.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
                    }}
                    // Thin out labels on longer windows so they don't collide.
                    interval={windowDays > 30 ? Math.floor(windowDays / 10) : (windowDays > 14 ? 2 : 0)}
                  />
                  <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: '#0A1024',
                      border: '1px solid #1F2A4D',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      fontSize: '12px',
                    }}
                    labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                  <Line
                    type="monotone" dataKey="count" name="Pauses"
                    stroke="#f43f5e" strokeWidth={2}
                    dot={{ r: 2 }} activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone" dataKey="resolved" name="Resolved"
                    stroke="#34d399" strokeWidth={2}
                    dot={{ r: 2 }} activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-platform breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-white">Per-Platform Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-rose-400" /></div>
          ) : !stats?.per_platform || stats.per_platform.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center" data-testid="admin-pause-empty-platforms">
              No pause events in this window. Nice — every monitor is holding up.
            </p>
          ) : (
            <div className="space-y-3">
              {stats.per_platform.map((p) => (
                <div
                  key={p.platform}
                  className="border border-[#1F2A4D] rounded-lg p-4 bg-[#0A1024]"
                  data-testid={`admin-pause-platform-${p.platform}`}
                >
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h3 className="text-white font-semibold capitalize">{p.platform}</h3>
                    <Badge className="bg-rose-950/50 text-rose-200 border-rose-900/40">
                      {p.total_pauses} total pause{p.total_pauses === 1 ? '' : 's'}
                    </Badge>
                    {p.active_pauses > 0 && (
                      <Badge className="bg-amber-950/50 text-amber-200 border-amber-900/40">
                        {p.active_pauses} still active
                      </Badge>
                    )}
                    {p.avg_time_to_resolve_hours !== null && p.avg_time_to_resolve_hours !== undefined && (
                      <span className="text-xs text-slate-500">
                        Avg time-to-resolve: {p.avg_time_to_resolve_hours}h
                      </span>
                    )}
                  </div>
                  {p.top_errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                        Top errors
                      </p>
                      <ul className="space-y-1">
                        {p.top_errors.map((err) => (
                          <li
                            key={err.error}
                            className="flex items-start gap-2 text-xs text-slate-300"
                            data-testid={`admin-pause-error-row-${p.platform}`}
                          >
                            <span className="text-rose-400 shrink-0 font-mono">{err.count}×</span>
                            <code className="text-slate-400 bg-[#0F1730] px-1.5 py-0.5 rounded truncate">
                              {err.error}
                            </code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent events table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-white">Recent Pause Events</CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="admin-pause-platform-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {platformOptions.map((p) => (
                  <SelectItem key={p} value={p}>{p === 'all' ? 'All platforms' : p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-rose-400" /></div>
          ) : events.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center" data-testid="admin-pause-empty-events">
              No pause events match this filter.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1F2A4D] text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="pb-2 pr-3">Platform</th>
                    <th className="pb-2 pr-3">Handle</th>
                    <th className="pb-2 pr-3">User</th>
                    <th className="pb-2 pr-3">Paused</th>
                    <th className="pb-2 pr-3">Error</th>
                    <th className="pb-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, idx) => (
                    <tr
                      key={`${ev.user_id}-${ev.platform}-${ev.paused_at}-${idx}`}
                      className="border-b border-[#1F2A4D]/60 text-slate-300"
                      data-testid={`admin-pause-event-row`}
                    >
                      <td className="py-2 pr-3 capitalize">{ev.platform}</td>
                      <td className="py-2 pr-3 truncate max-w-[140px]">@{ev.handle}</td>
                      <td className="py-2 pr-3 truncate max-w-[180px] text-slate-400">{ev.user_email || '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-500">{relative(ev.paused_at)}</td>
                      <td className="py-2 pr-3 truncate max-w-[240px] text-slate-400 font-mono text-xs">
                        {ev.error || '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {ev.resolved_at ? (
                          <Badge className="bg-emerald-950/40 text-emerald-300 border-emerald-900/40">
                            Resolved
                          </Badge>
                        ) : (
                          <Badge className="bg-rose-950/40 text-rose-300 border-rose-900/40">
                            Active
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PauseHistoryPage;
